"use client";

import { useState } from "react";
import {
  ArrowUpRight,
  Check,
  Code2,
  FileSearch,
  Globe2,
  Link2,
  ListChecks,
  Search,
} from "lucide-react";
import type { SeoAnalysisCopy } from "./seo-specialist-copy";
import styles from "./seo-specialist.module.scss";

/** CSS perspective keeps the layered illustration sharp without a WebGL bundle. */
export function CcSeoLayeredHero({
  copy,
  technical = false,
}: {
  copy: SeoAnalysisCopy["hero"];
  technical?: boolean;
}) {
  const [active, setActive] = useState(3);
  const icons = technical
    ? [Link2, Globe2, Code2, Search]
    : [Globe2, Code2, FileSearch, ListChecks];

  return (
    <div className={styles.heroArt}>
      <div className={styles.stageTop}>
        <span>
          <i />
          {copy.stageLabel}
        </span>
      </div>
      <div className={styles.layerStage}>
        <div className={styles.orbit} aria-hidden="true" />
        <div className={styles.orbitInner} aria-hidden="true" />
        <div className={styles.layers}>
          {copy.panels.map((panel, index) => {
            const Icon = icons[index];
            return (
              <button
                type="button"
                className={styles.layer}
                data-active={active === index}
                data-layer={index}
                key={panel.label}
                onClick={() => setActive(index)}
                aria-pressed={active === index}
              >
                <span className={styles.layerLabel}>
                  {panel.label}
                  <ArrowUpRight size={14} aria-hidden="true" />
                </span>
                <div className={styles.layerDrawing} aria-hidden="true">
                  {index === 0 ? (
                    <div className={styles.wirePage}>
                      <div />
                      <span />
                      <span />
                      <section>
                        <i />
                        <i />
                        <i />
                      </section>
                      <span />
                    </div>
                  ) : index === 1 ? (
                    <div className={styles.codeDrawing}>
                      <Code2 size={43} strokeWidth={1.3} />
                      <span />
                      <span />
                      <span />
                      <i />
                    </div>
                  ) : index === 2 ? (
                    <div className={styles.searchDrawing}>
                      <div>
                        <Search size={17} />
                        <span />
                      </div>
                      <i />
                      <i />
                      <i />
                    </div>
                  ) : (
                    <div className={styles.checkDrawing}>
                      {[0, 1, 2].map((n) => (
                        <div key={n}>
                          <Check size={17} />
                          <span />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <span className={styles.layerBottom}>
                  <Icon size={22} strokeWidth={1.5} aria-hidden="true" />
                  <strong>{panel.title}</strong>
                  <small>{panel.status}</small>
                </span>
                {index === 1 && (
                  <span className={styles.scanLine} aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className={styles.stageBottom}>
        <p aria-live="polite">
          <span>{String(active + 1).padStart(2, "0")}</span>
          {copy.panels[active].detail}
        </p>
        <div className={styles.stageDots} aria-hidden="true">
          {copy.panels.map((p, i) => (
            <i key={p.label} data-active={active === i} />
          ))}
        </div>
      </div>
      <small className={styles.artNote}>{copy.stageNote}</small>
    </div>
  );
}
